export interface VfsFile {
    type: "file";
    content: string;
    size: number;
    mtime: string;
    permissions?: string;
    imageSrc?: string;
    downloadSrc?: string;
}

export interface VfsDir {
    type: "dir";
    children: Record<string, VfsEntry>;
    permissions?: string;
}

export type VfsEntry = VfsFile | VfsDir;

function isHiddenName(name: string): boolean {
    return name.startsWith(".");
}

export function listDirNames(children: Record<string, VfsEntry>, showHidden: boolean): string[] {
    return Object.keys(children)
        .filter((name) => showHidden || !isHiddenName(name))
        .sort();
}

function normalizePath(path: string): string {
    const parts = path.split("/").filter(Boolean);
    const stack: string[] = [];

    for (const part of parts) {
        if (part === ".") continue;
        if (part === "..") {
            stack.pop();
            continue;
        }
        stack.push(part);
    }

    return stack.length === 0 ? "/" : `/${stack.join("/")}`;
}

function joinPath(base: string, segment: string): string {
    if (segment.startsWith("/")) return normalizePath(segment);
    const baseParts = base === "/" ? [] : base.split("/").filter(Boolean);
    const segParts = segment.split("/").filter(Boolean);
    return normalizePath(`/${[...baseParts, ...segParts].join("/")}`);
}

function findChild(dir: VfsDir, part: string): VfsEntry | undefined {
    const direct = dir.children[part];
    if (direct) return direct;

    const lower = part.toLowerCase();
    for (const [name, entry] of Object.entries(dir.children)) {
        if (name.toLowerCase() === lower) return entry;
    }

    return undefined;
}

export class VfsSession {
    readonly home: string;
    cwd: string;
    private readonly homeEntries: Record<string, VfsEntry>;

    constructor(homeEntries: Record<string, VfsEntry>, home: string) {
        this.homeEntries = homeEntries;
        this.home = home;
        this.cwd = home;
    }

    isPathAllowed(path: string): boolean {
        return path === this.home || path.startsWith(`${this.home}/`);
    }

    formatPrompt(handle: string, hostname: string): string {
        const display =
            this.cwd === this.home ? "~" : `~${this.cwd.slice(this.home.length)}`;
        return `${handle}@${hostname}:${display}$`;
    }

    resolvePath(raw: string): string {
        const target = raw.trim();
        if (!target || target === "~") return this.home;
        if (target.startsWith("~/")) return normalizePath(this.home + target.slice(1));
        if (target.startsWith("/")) return normalizePath(target);
        return joinPath(this.cwd, target);
    }

    lookup(path: string): VfsEntry | null {
        if (!this.isPathAllowed(path)) return null;
        if (path === this.home) {
            return { type: "dir", children: this.homeEntries };
        }

        const relative = path.slice(this.home.length + 1);
        if (!relative) return null;

        const parts = relative.split("/").filter(Boolean);
        let current: VfsEntry = { type: "dir", children: this.homeEntries };

        for (const part of parts) {
            if (current.type !== "dir") return null;
            const child = findChild(current, part);
            if (!child) return null;
            current = child;
        }

        return current;
    }

    listDir(path: string): VfsEntry | null {
        const entry = this.lookup(path);
        return entry?.type === "dir" ? entry : null;
    }
}
